import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {DragDropContext} from 'react-beautiful-dnd';

import {selectWisbit} from 'store/selectors';
import {OPERATE_TYPES} from 'api/constants';
import {MODE} from 'containers/App/constants';
import {Title} from 'components';

import {Bit} from './types';
import {moveBit} from './redux/actions';
import {INTERACTIVE, FIELDS} from './constants';
import {saveWisbit} from './redux/thunks';

import DroppableBits from './DroppableBits';
import i18n from 'i18next';

type BitsProps = {
  mode: string;
  bits?: Bit[];
};

function Bits({mode}: BitsProps) {
  const {bits} = useSelector(selectWisbit);
  const dispatch = useDispatch();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onDragEnd = (result: any) => {
    const {source, destination, draggableId, type} = result;

    if (!bits || !destination) {
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (type === 'bit') {
      const newBits = [...bits];

      const draggedBit = bits.find((_bit, index) => index === source.index);

      if (!draggedBit) {
        return;
      }

      newBits.splice(source.index, 1);
      newBits.splice(destination.index, 0, draggedBit);

      dispatch(moveBit(newBits));

      dispatch(
        saveWisbit({
          op: OPERATE_TYPES.MOVE,
          field: FIELDS.BITS,
          from: {
            bitIndex: source.index,
          },
          to: {
            bitIndex: destination.index,
          },
        })
      );

      return;
    }

    const startIndex = bits.findIndex(({id}) => id === source.droppableId);
    const endIndex = bits.findIndex(({id}) => id === destination.droppableId);

    const start = bits[startIndex];
    const end = bits[endIndex];

    if (!start || !end) {
      return;
    }

    const {components: startComponents} = start;
    if (!startComponents) {
      return;
    }

    const {components: endComponents} = end;
    if (!endComponents) {
      return;
    }

    const draggedComponent = startComponents.find(({id}) => id === draggableId);

    if (!draggedComponent) {
      return;
    }

    let newBits: Bit[] = [];

    if (start.id === end.id) {
      const newComponents = [...startComponents];

      newComponents.splice(source.index, 1);
      newComponents.splice(destination.index, 0, draggedComponent);

      const newBit = {
        ...start,
        components: newComponents,
      };

      newBits = bits.map(bit => {
        if (bit.id === start.id) {
          return newBit;
        } else {
          return bit;
        }
      });
    } else {
      const startComponentType = startComponents[source.index].type;
      if (startComponentType && INTERACTIVE.includes(startComponentType)) {
        const hasInterfactive =
          endComponents.findIndex(
            ({type}) => type && INTERACTIVE.includes(type)
          ) > -1;
        if (hasInterfactive) {
          return;
        }
      }

      const newStartComponents = [...startComponents];
      newStartComponents.splice(source.index, 1);

      const newStart = {
        ...start,
        components: newStartComponents,
      };

      const newEndComponents = [...endComponents];
      newEndComponents.splice(destination.index, 0, draggedComponent);

      const newEnd = {
        ...end,
        components: newEndComponents,
      };

      newBits = bits.map(bit => {
        if (bit.id === start.id) {
          return newStart;
        } else if (bit.id === end.id) {
          return newEnd;
        } else {
          return bit;
        }
      });
    }

    dispatch(moveBit(newBits));

    dispatch(
      saveWisbit({
        op: OPERATE_TYPES.MOVE,
        field: FIELDS.BITS,
        from: {
          bitIndex: startIndex,
          componentIndex: source.index,
        },
        to: {
          bitIndex: endIndex,
          componentIndex: destination.index,
        },
      })
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="bg-white rounded-gigantic w-2/3 p-8 h-fit">
        <Title className="pb-6">{i18n.t('wisbitPage.bits.title')}</Title>
        <DroppableBits disabled={mode === MODE.VIEW} />
      </div>
    </DragDropContext>
  );
}

export default Bits;
